// Session Timeout Component - drbooking
// Déconnexion automatique après inactivité pour sécurité

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Clock, AlertTriangle, LogOut } from 'lucide-react';

const SessionTimeout = ({ 
  timeoutMinutes = 30,  // Durée d'inactivité avant déconnexion
  warningMinutes = 5     // Minutes avant d'afficher l'avertissement
}) => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningMinutes * 60);
  
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  
  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;

  // Reset all timers
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowWarning(false);
    setRemainingTime(warningMinutes * 60);
    
    if (!isAuthenticated) return;
    
    // Set warning timer (shows warning before timeout)
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setRemainingTime(warningMinutes * 60);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, timeoutMs - warningMs);
    
    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleLogout();
    }, timeoutMs);
    
  }, [isAuthenticated, timeoutMs, warningMs, warningMinutes]);

  // Handle logout
  const handleLogout = useCallback(async () => {
    setShowWarning(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    await logout();
    navigate('/login', { 
      state: { message: 'Votre session a expiré. Veuillez vous reconnecter.' }
    });
  }, [logout, navigate]);

  // Extend session
  const extendSession = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Listen for user activity
  useEffect(() => {
    if (!isAuthenticated) {
      // Clean up when logged out
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setShowWarning(false);
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle activity detection
    let activityTimeout;
    const handleActivity = () => {
      if (activityTimeout) return;
      activityTimeout = setTimeout(() => {
        activityTimeout = null;
        if (!showWarning) {
          resetTimers();
        }
      }, 1000);
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize timers
    resetTimers();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, [isAuthenticated, resetTimers, showWarning]);

  // Don't render if not authenticated or no warning
  if (!isAuthenticated || !showWarning) return null;

  // Format remaining time
  const minutes = Math.floor(remainingTime / 60);
  const seconds = remainingTime % 60;
  const timeString = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="session-timeout-modal"
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Session inactive
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Votre session va expirer
            </p>
          </div>
        </div>

        {/* Timer */}
        <div className="bg-slate-100 dark:bg-slate-700 rounded-xl p-6 text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-slate-500" />
            <span className="text-sm text-slate-600 dark:text-slate-300">
              Déconnexion automatique dans
            </span>
          </div>
          <div className="text-4xl font-mono font-bold text-orange-600 dark:text-orange-400">
            {timeString}
          </div>
        </div>

        {/* Message */}
        <p className="text-sm text-slate-600 dark:text-slate-300 mb-6 text-center">
          Pour des raisons de sécurité, vous serez déconnecté après{' '}
          <strong>{timeoutMinutes} minutes</strong> d'inactivité.
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={handleLogout}
            data-testid="session-logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Se déconnecter
          </Button>
          <Button
            className="flex-1 bg-blue-600 hover:bg-blue-700"
            onClick={extendSession}
            data-testid="session-extend-btn"
          >
            Rester connecté
          </Button>
        </div>

        {/* User info */}
        {user && (
          <p className="text-xs text-slate-400 text-center mt-4">
            Connecté en tant que {user.email}
          </p>
        )}
      </div>
    </div>
  );
};

export default SessionTimeout;
